<template>
  <div class="create-password-container">
    <div class="container-title">{{ $t('login.common.title.createPassword' ) }}</div>
    <div class="content-form create-password-form">
      <div class="form-line has-sub-form-line">
        <div class="sub-form-line">
          <el-input
            v-model="firstName"
            class="line-input"
            :placeholder="$t('login.common.label.firstName')"
            disabled
          ></el-input>
        </div>
        <div class="sub-form-line" :class="hasError(message.lastName) && 'has-error'">
          <el-input
            v-model="lastName"
            class="line-input"
            :placeholder="$t('login.common.label.lastName')"
            disabled
          ></el-input>
        </div>
      </div>
      <div class="form-line">
        <el-input
          v-model="email"
          class="line-input"
          :placeholder="$t('login.common.label.email')"
          disabled
        ></el-input>
      </div>
      <div class="form-line" :class="hasError(message.phone) && 'has-error'">
        <el-input
          v-model="phone"
          class="line-input"
          :placeholder="$t('login.common.label.phone')"
          disabled
        ></el-input>
      </div>
      <div class="form-line" :class="hasError(message.password) && 'has-error'">
        <el-input
          v-model="password"
          ref="password"
          class="line-input"
          autocomplete="new-password"
          :type="viewPassword ? 'text' : 'password'"
          :placeholder="$t('login.common.placeholder.password')"
          @focus="clearErrorMessage(message.password)"
          @blur="validate(message.password)"
          @change="validate(message.password)"
        >
          <template #suffix>
            <i class="password-icon iconfont" :class="viewPassword ? 'iconVisible' : 'iconHide1'" @click="onViewPassword"></i>
          </template>
        </el-input>
        <div v-show="message.password.show" class="line-tip">{{ $t(message.password.textI18nKey) }}</div>
      </div>
      <div class="form-line" :class="hasError(message.confirmPassword) && 'has-error'">
        <el-input
          v-model="confirmPassword"
          ref="password"
          class="line-input"
          autocomplete="new-password"
          :type="viewConfirmPassword ? 'text' : 'password'"
          :placeholder="$t('login.common.placeholder.confirmPassword')"
          @focus="clearErrorMessage(message.confirmPassword)"
          @blur="validate(message.confirmPassword)"
          @change="validate(message.confirmPassword)"
        >
          <template #suffix>
            <i class="password-icon iconfont" :class="viewConfirmPassword ? 'iconVisible' : 'iconHide1'" @click="onViewConfirmPassword"></i>
          </template>
        </el-input>
        <div v-show="message.confirmPassword.show" class="line-tip">{{ $t(message.confirmPassword.textI18nKey) }}</div>
      </div>
    </div>
    <div class="button-container">
      <el-button class="submit-btn" type="primary" @click="onSubmitBtnClick">{{ $t('common.btn.submit') }}</el-button>
    </div>
    <div class="form-bottom">
      <div class="bottom-line">
        <el-checkbox v-model="agree" class="policy-checkbox">
          <span>{{ $t('login.common.message.policy.tip') }}</span>
          <a class="link" href="/tos/" target="_blank">{{ $t('login.common.message.policy.userAgreement') }}</a>
          <span> & </span>
          <a class="link" href="/policy/" target="_blank">{{ $t('login.common.message.policy.privacyPolicy') }}</a>
        </el-checkbox>
        <div class="line-tip"></div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { mixedRouterPush } from '@/router/routes'
import { validatePassword } from '@/utils/validator'
import { ACCOUNT_TYPE, HM_MOBILE_DASHBOARD_PATH, HM_PC_DASHBOARD_PATH } from '@/constant/common'
import { passwordEncrypt } from '@/utils/rsa'
import { deviceType } from '@/utils/common'

export default {
  props: {
    company: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      password: null,
      confirmPassword: null,
      viewPassword: false,
      viewConfirmPassword: false,
      agree: true,
      message: {
        password: {
          show: false,
          isError: false,
          textI18nKey: null
        },
        confirmPassword: {
          show: false,
          isError: false,
          textI18nKey: null
        }
      }
    }
  },
  computed: {
    accountType () {
      return this.$route.params.accountType
    },
    createCode () {
      return this.$route.params.code
    }
  },
  created () {
    this.checkCreateCode()
  },
  methods: {
    ...mapActions('account', ['createPassword', 'getCreatePasswordCodeInfo']),
    ...mapActions('hm', ['createHmPassword', 'getHmInfoByResetCode']),
    async checkCreateCode () {
      this.$eventBus.$emit('showLoading', true)
      try {
        let resp
        if (this.accountType === ACCOUNT_TYPE.HIRING_MANAGER) {
          resp = await this.getHmInfoByResetCode(this.createCode)
        } else {
          resp = await this.getCreatePasswordCodeInfo({
            code: this.createCode
          })
        }
        if (resp) {
          this.firstName = resp.first_name
          this.lastName = resp.last_name
          this.email = resp.email
          this.phone = resp.phone
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    hasError (message) {
      return message && message.show && message.isError
    },
    setErrorMessage (message, textI18nKey, getter) {
      if (getter) {
        Object.defineProperty(message, 'text', {
          configurable: true,
          enumerable: true,
          get: getter
        })
      } else {
        message.text = null
      }
      message.isError = true
      message.textI18nKey = textI18nKey
      message.show = true
    },
    clearErrorMessage (message) {
      message.show = false
      message.isError = false
      message.text = null
      message.textI18nKey = null
    },
    onViewPassword () {
      this.viewPassword = !this.viewPassword
    },
    onViewConfirmPassword () {
      this.viewConfirmPassword = !this.viewConfirmPassword
    },
    validate (message) {
      if (!message) {
        return !_.chain(this.message).map(this.validate).includes(false).value()
      }
      let hasError = false
      let textI18nKey = null
      const getter = null
      if (message === this.message.password) {
        if (this.password) {
          if (validatePassword(null, this.password)) {
            hasError = true
            textI18nKey = 'login.common.message.passwordRule'
          }
        } else {
          hasError = true
          textI18nKey = 'login.common.placeholder.password'
        }
      } else if (message === this.message.confirmPassword) {
        if (this.confirmPassword) {
          if (validatePassword(null, this.confirmPassword)) {
            hasError = true
            textI18nKey = 'login.common.message.passwordRule'
          } else if (this.confirmPassword !== this.password) {
            hasError = true
            textI18nKey = 'login.common.message.notSamePassword'
          } else if (this.message.password.isError && this.message.password.textI18nKey === 'login.common.message.notSamePassword') {
            this.clearErrorMessage(this.message.password)
          }
        } else {
          hasError = true
          textI18nKey = 'login.common.placeholder.confirmPassword'
        }
      }
      if (hasError) {
        this.setErrorMessage(message, textI18nKey, getter)
        return false
      } else {
        this.clearErrorMessage(message)
        return true
      }
    },
    async onSubmitBtnClick () {
      if (!this.agree) {
        this.$message.warning(this.$t('login.common.message.agreePolicyFirst'))
        return
      }
      if (this.accountType === ACCOUNT_TYPE.HIRING_MANAGER) {
        this.onHMCreatePassword()
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        if (!this.validate()) {
          return
        }
        const resp = await this.createPassword({
          code: this.createCode,
          password: passwordEncrypt(this.password),
          confirm_password: passwordEncrypt(this.confirmPassword),
          back: this.$route.query.back
        })
        if (resp) {
          this.$message.success({ message: this.$t('login.common.message.createPasswordSuccess'), duration: 5000, showClose: true })
          const nextPage = this.$route.query.back || resp.next_page || `/passport/${this.accountType}/login`
          mixedRouterPush(nextPage)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async onHMCreatePassword () {
      this.$eventBus.$emit('showLoading', true)
      try {
        if (!this.validate()) {
          return
        }
        const resp = await this.createHmPassword({
          resetCode: this.createCode,
          formData: {
            password: passwordEncrypt(this.password),
            confirm_password: passwordEncrypt(this.confirmPassword)
          }
        })
        if (resp) {
          this.$message.success({ message: this.$t('login.common.message.createPasswordSuccess'), duration: 5000, showClose: true })
          mixedRouterPush(this.$route.query.back || (deviceType.isPc ? HM_PC_DASHBOARD_PATH : HM_MOBILE_DASHBOARD_PATH))
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.create-password-container {
  .create-password-form {
    margin-top: 35px;

    .form-line {
      min-height: 40px;
      margin-bottom: 24px;

      &.has-error {
        margin-bottom: 6px;

        .line-tip {
          padding-left: 0;
          color: #f56c6c;
        }

        .line-input {
          ::v-deep {
            > .el-input__inner {
              border-color: #f56c6c;
            }

            .el-input__inner:hover + .el-input-group__prepend,
            .el-input__inner:hover + .el-input-group__append,
            .el-input-group__prepend,
            .el-input-group__append {
              border-color: #f56c6c;
            }
          }
        }
      }

       .sub-form-line {
        display: inline-block;
        width: 190px;
        height: 40px;
        vertical-align: middle;

        &.has-error {
          .line-tip {
            padding-left: 0;
            color: #f56c6c;
          }

          .line-input {
            ::v-deep {
              .el-input__inner {
                border-color: #f56c6c;
              }
            }
          }
        }

        + .sub-form-line {
          margin-left: 20px;
        }
      }

      .line-input {
        ::v-deep {
          .el-input__inner {
            border-radius: 2px;
            padding-left: 14px;
            padding-right: 35px;
          }

          .el-input__suffix {
            right: 14px;
          }
        }

        .password-icon {
          height: 40px;
          line-height: 40px;
          cursor: pointer;
          font-size: 14px;
          color: #909399;
        }
      }

      .line-tip {
        padding-left: 15px;
        font-size: 12px;
        line-height: 18px;
      }
    }
  }

  .button-container {

    .el-button {
      width: 100%;
      padding: 9px 0;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      text-align: center;
      border: 0;
    }
  }

  .form-bottom {
    margin-top: 26px;

    .bottom-line {
      margin-bottom: 10px;
    }

    .policy-checkbox {
      white-space: nowrap;

      ::v-deep {
        .el-checkbox__inner {
          width: 12px;
          height: 12px;

          &::after {
            top: 0;
            left: 3px;
          }
        }

        .el-checkbox__label {
          white-space: initial;
          word-break: break-word;
          vertical-align: text-top;
          color: #606266;
          line-height: 19px;
          font-size: 12px;
          font-weight: 400px;
        }
      }
    }
  }
}
.is-mobile {
  .create-password-container {
    .container-title {
      font-size: 30px;
      line-height: 44px;
    }

    .create-password-form {
      .form-line {

        &.has-sub-form-line {
          display: flex;

          .sub-form-line {
            flex: 1;
            width: auto;
          }
        }
      }
    }
  }
}
</style>
